'use client';
import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { DialogTrigger } from '@radix-ui/react-dialog';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { useAccount } from '@/frontastic';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { UpdateOrderPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../customer-detail/constants';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { useToast } from '../../ui/use-toast';

const AdvanceShippingOptionsModal = ({
  Texts,
  showAdvanceShippingOption,
  isSignatureRequired,
}: {
  Texts: { [key: string]: string };
  showAdvanceShippingOption: boolean;
  isSignatureRequired?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const params = useSearchParams();
  const [signatureRequired, setSignatureRequired] = useState(isSignatureRequired);
  const { updateOrder } = useAccount();
  const [loading, setLoading] = useState(false);

  const handleSignatureRequiredChange = (event: any) => {
    setSignatureRequired(event.target.checked);
  };

  const handleUpdate = async () => {
    setLoading(true);
    const payload = {
      query: {
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      },
      data: {
        Recipients: [
          {
            RecipientInfo: {
              isSignatureRequired: signatureRequired,
            },
          },
        ],
      },
    } as UpdateOrderPayload;
    await updateOrder(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        SuccessNotification(toast, {
          description: Texts.shippingOptionUpdated,
          action: <SuccessIcon className="h-5 w-5" />,
        });
      } else {
        ErrorNotification(toast, {
          description: Texts.shippingOptionUpdateFail,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      }
    });
    setLoading(false);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) {
          setSignatureRequired(false);
        }
      }}
    >
      <DialogTrigger className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium">
        {Texts.advanceShippingOption}
      </DialogTrigger>
      <DialogContent className="max-w-[520px] bg-white sm:rounded-md">
        {loading && <Loader />}
        <DialogHeader>
          {/* chng-text-25px */}
          <DialogTitle className="text-2xl font-bold">
            <div className="text-2xl font-bold">{Texts.advanceShippingOption}</div>
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={signatureRequired}
              onChange={handleSignatureRequiredChange}
              className="mr-2"
              disabled={!showAdvanceShippingOption}
            />
            {Texts.signatureRequired}
          </label>
        </div>
        <section className="mt-4 flex justify-start gap-4">
          <Button
            className="rounded-md bg-deepBlue p-4 text-white"
            onClick={() => handleUpdate()}
            disabled={!showAdvanceShippingOption}
          >
            {Texts.update}
          </Button>
          <Button className="p-0 underline" onClick={() => setOpen(false)}>
            {Texts.cancel}
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default AdvanceShippingOptionsModal;
